import React, { useEffect, useRef, useState } from "react";
import { addMonths, isTrue, subtractMonths } from "./../common/utilities";

import { ReactComponent as BackIcon } from "../../components/icons/arrow_icon.svg";
import Error from "../common/ServerError";
import ForecastChart from "./DbForecastChart";
import ForecastTable from "./DbForecastTable";
import ItemAreaForecastTable from "./DbItemAreaForecastTable";
import OverrideOptions from "../override-options/OverrideOptions";
import {
  FileSpreadsheet,
  List,
  Pencil,
  RefreshCw,
  LayoutList,
  Table,
  CheckCheck,
} from "lucide-react";
import Spinner from "../common/Spinner";
import * as XLSX from "xlsx";
import applyToTimeBuckets from "../../constants/applyToTimeBuckets";
import { createPortal } from "react-dom";
import dateHelper from "../common/dateHelper";
import entityName from "../../constants/entityName";
import forecastApi from "../../api/forecastApi";
import forecastViewType from "../../constants/forecastViewType";
import overrideOptions from "../../constants/overrideOptions";
import { tokenRequest } from "../../authConfig";
import { useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import PendingOverridesToggle from "./PendingOverridesToggle";

const StatusToast = ({ id, type, text, onDismiss, onRefresh }) => {
  return (
    <div className={`forecast-status-toast forecast-status-toast--${type}`}>
      <span className="forecast-status-toast__text">{text}</span>
      {type === "success" && onRefresh ? (
        <button
          type="button"
          className="forecast-status-toast__refresh"
          onClick={onRefresh}
        >
          <RefreshCw size={14} strokeWidth={2} />
          <span>Refresh</span>
        </button>
      ) : null}
      <button
        type="button"
        className="forecast-status-toast__close"
        onClick={() => onDismiss(id)}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
};

const DbForecast = ({
  config,
  selectedSearchData,
  showChart,
  currentAction,
  setCurrentAction,
  handleSearchCallback,
  checkDataEditLock,
  setAuthorizationError,
  handleBack,
  isUserAuthorized,
  currentView,
  hideBackButton,
}) => {
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(false);
  const [results, setResults] = useState({});
  const [request, setRequest] = useState(null);
  const { instance, accounts } = useMsal();
  const [, , getDateInUTC] = dateHelper();
  const previousViewRef = useRef(null);
  const pollingTimersRef = useRef(new Map());
  const hasInitialData = useRef(false);
  const [dataVersion, setDataVersion] = useState(0);
  const [refetchNonce, setRefetchNonce] = useState(0);
  const [saveSuccessNonce, setSaveSuccessNonce] = useState(0);
  const [errorMessages, setErrorMessages] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessages, setStatusMessages] = useState([]);
  const [saveOnlyChangedTimeBuckets, setSaveOnlyChangedTimeBuckets] = useState(
    config && config.defaultApplyToTimeBucketsValue === applyToTimeBuckets.All
      ? false
      : true,
  );

  const [options, setOptions] = useState([
    {
      value: overrideOptions.RemoveOverrides,
      description: overrideOptions.RemoveOverrides,
      label:
        "Overrides will be replaced with the statistical forecast for the selection",
      isSelected: false,
      featureEnabled: true,
    },
    {
      value: overrideOptions.AdvancedOverrides,
      description: overrideOptions.AdvancedOverrides,
      label: "Overrides will be calculated based on the parameters",
      isSelected: false,
      featureEnabled: isTrue(config.enableAdvancedForecast),
    },
  ]);

  const [showOptionsModal, setShowOptionsModal] = useState(false);

  const [enableOptions, setEnableOptions] = useState(
    currentAction?.attributeConfiguration?.parent === entityName.Forecast,
  );

  const [selectedOptions, setSelectedOptions] = useState(null);

  const PENDING_OVERRIDES_STORAGE_KEY = "forecast.includePendingOverrides";

  const [includePendingOverrides, setIncludePendingOverrides] = useState(() => {
    try {
      return localStorage.getItem(PENDING_OVERRIDES_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const handlePendingOverridesChange = (next) => {
    setIncludePendingOverrides(next);
    try {
      localStorage.setItem(PENDING_OVERRIDES_STORAGE_KEY, String(next));
    } catch {}
  };

  const buildRequest = () => {
    const itemAreaLevel =
      config?.isItemAreaForecastEditable &&
      currentAction != null &&
      currentAction.areaCode != null &&
      currentAction.item != null;

    const baseRequest = {
      path: "forecast",
      field: config.fields,
      selectedFacetsFilterData: selectedSearchData?.selectedFilterData,
      afterKey: { value: "", description: "" },
      query: "",
      unitOfMeasurements: selectedSearchData?.unitOfMeasurements,
      currency: selectedSearchData?.currency,
      itemAreaLevel,
    };

    switch (currentView) {
      case forecastViewType.BudgetView: {
        const budgetViewFromDate = getDateInUTC(
          config.budgetViewForecastFromDate,
        );
        const budgetViewToDate = getDateInUTC(config.budgetViewForecastToDate);
        return {
          ...baseRequest,
          fromDate: budgetViewFromDate,
          toDate: budgetViewToDate,
          forecastPercentageBaseField: config.forecastPercentageBaseField,
          forecastTotalFromDate: budgetViewFromDate,
          forecastTotalToDate: budgetViewToDate,
          forecastTotalByDateHeader: config.budgetViewForecastTotalByDateHeader,
          forecastTotalByDatePercentage:
            config.budgetViewForecastTotalByDatePercentage,
        };
      }
      case forecastViewType.FutureView: {
        const futureViewFromDate = getDateInUTC(
          config.futureViewForecastFromDate,
        );
        const futureViewToDate = getDateInUTC(config.futureViewForecastToDate);
        return {
          ...baseRequest,
          fromDate: futureViewFromDate,
          toDate: futureViewToDate,
          forecastPercentageBaseField: config.forecastPercentageBaseField,
          forecastTotalFromDate: futureViewFromDate,
          forecastTotalToDate: futureViewToDate,
          forecastTotalByDateHeader: config.futureViewForecastTotalByDateHeader,
          forecastTotalByDatePercentage:
            config.futureViewForecastTotalByDatePercentage,
        };
      }
      case forecastViewType.HistoricView: {
        const historicViewFromDate = getDateInUTC(config.historicViewFromDate);
        const historicViewToDate = getDateInUTC(config.historicViewToDate);
        return {
          ...baseRequest,
          fromDate: historicViewFromDate,
          toDate: historicViewToDate,
          historyFromDate: historicViewFromDate,
          historyToDate: historicViewToDate,
          forecastPercentageBaseField: config.forecastPercentageBaseField,
          forecastTotalFromDate: historicViewFromDate,
          forecastTotalToDate: historicViewToDate,
          forecastTotalByDateHeader: config.futureViewForecastTotalByDateHeader,
          forecastTotalByDatePercentage:
            config.futureViewForecastTotalByDatePercentage,
          shouldGetSpecificHistory: true,
        };
      }
      default: {
        const startDateInUTC =
          config.forecastTotalFromDate != null &&
          config.forecastTotalToDate != null
            ? getDateInUTC(config.forecastTotalFromDate)
            : null;
        const endDateInUTC =
          config.forecastTotalFromDate != null &&
          config.forecastTotalToDate != null
            ? getDateInUTC(config.forecastTotalToDate)
            : null;
        const startDate2InUTC =
          config.forecastTotal2FromDate != null &&
          config.forecastTotal2ToDate != null
            ? getDateInUTC(config.forecastTotal2FromDate)
            : null;
        const endDate2InUTC =
          config.forecastTotal2FromDate != null &&
          config.forecastTotal2ToDate != null
            ? getDateInUTC(config.forecastTotal2ToDate)
            : null;
        return {
          ...baseRequest,
          fromDate: subtractMonths(config.forecastFromMonths),
          toDate: addMonths(config.forecastToMonths),
          forecastTotalToMonths: config.forecastTotalToMonths,
          forecastYearToGo: config.forecastYearToGo,
          forecastTotalToMonthsPercentageDifference:
            config.forecastTotalToMonthsPercentageDifference,
          forecastYearToGoPercentageDifference:
            config.forecastYearToGoPercentageDifference,
          forecastPercentageBaseField: config.forecastPercentageBaseField,
          forecastTotalFromDate: startDateInUTC,
          forecastTotalToDate: endDateInUTC,
          forecastTotalByDateHeader: config.forecastTotalByDateHeader,
          forecastTotalByDatePercentage: config.forecastTotalByDatePercentage,
          forecastTotal2FromDate: startDate2InUTC,
          forecastTotal2ToDate: endDate2InUTC,
          forecastTotal2ByDateHeader: config.forecastTotal2ByDateHeader,
          forecastTotal2ByDatePercentage: config.forecastTotal2ByDatePercentage,
        };
      }
    }
  };

  useEffect(() => {
    if (!config || !config?.fields?.length > 0) {
      return;
    }

    if (!selectedSearchData || !selectedSearchData.unitOfMeasurements) {
      return;
    }

    const viewChanged = previousViewRef.current !== currentView;
    previousViewRef.current = currentView;

    setRequest((req) => {
      if (req == null || viewChanged) {
        return buildRequest();
      } else if (
        JSON.stringify(req.selectedFacetsFilterData) !==
          JSON.stringify(selectedSearchData.selectedFilterData) ||
        JSON.stringify(req.unitOfMeasurements) !==
          JSON.stringify(selectedSearchData.unitOfMeasurements) ||
        JSON.stringify(req.currency) !==
          JSON.stringify(selectedSearchData.currency)
      ) {
        return {
          ...req,
          selectedFacetsFilterData: selectedSearchData.selectedFilterData,
          unitOfMeasurements: selectedSearchData.unitOfMeasurements,
          currency: selectedSearchData.currency,
        };
      } else {
        return {
          ...req,
        };
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSearchData, config, currentAction, currentView]);

  const withCalculatedFields = (data) => {
    if (!data || !Array.isArray(data.forecast)) {
      return data;
    }
    const calculatedConfigs = config.attributeConfigurations.filter(
      (c) =>
        typeof c.calculatedFields === "string" &&
        c.calculatedFields.trim().length > 0,
    );
    if (calculatedConfigs.length === 0) {
      return data;
    }
    return {
      ...data,
      forecast: data.forecast.map((item) => {
        if (item.isTotal || item.isPercentage) {
          return item;
        }
        const next = { ...item };
        for (const c of calculatedConfigs) {
          const sources = c.calculatedFields
            .split(",")
            .map((s) => s.trim().toLowerCase())
            .filter((s) => s.length > 0);
          let total = 0;
          for (const field of sources) {
            const value = next[field];
            if (value == null) continue;
            const num = Number(value);
            if (Number.isFinite(num)) {
              total += num;
            }
          }
          next[c.attributeName.toLowerCase()] = total;
        }
        return next;
      }),
    };
  };

  const withTotals = (data) => {
    if (!data || !Array.isArray(data.forecast)) {
      return data;
    }
    if (!request || !config || !Array.isArray(config.attributeConfigurations)) {
      return data;
    }

    const totalToMonths = Number(request.forecastTotalToMonths) || 0;
    const forecastTotalByDate = !!(
      request.forecastTotalFromDate && request.forecastTotalToDate
    );
    const forecastTotal2ByDate = !!(
      request.forecastTotal2FromDate && request.forecastTotal2ToDate
    );

    const totalsTriggered =
      isTrue(request.forecastYearToGo) ||
      isTrue(request.forecastYearToGoPercentageDifference) ||
      totalToMonths > 0 ||
      forecastTotalByDate ||
      forecastTotal2ByDate;

    if (!totalsTriggered) {
      return data;
    }

    const dateRows = data.forecast.filter((f) => !f.isTotal && !f.isPercentage);

    const addMonthsUTC = (months) => {
      const now = new Date();
      const originalDay = now.getUTCDate();
      const result = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + months, originalDay),
      );
      if (result.getUTCDate() !== originalDay) {
        result.setUTCDate(0);
      }
      return result;
    };

    const stripTime = (d) => {
      const copy = new Date(d);
      copy.setUTCHours(0, 0, 0, 0);
      return copy;
    };

    const nowDate = new Date();
    const currentYear = nowDate.getUTCFullYear();

    const totalToDate =
      totalToMonths > 0 ? stripTime(addMonthsUTC(totalToMonths - 1)) : null;
    const decemberDate = new Date(Date.UTC(currentYear, 11, 31));

    const ftFromDate = forecastTotalByDate
      ? stripTime(new Date(request.forecastTotalFromDate))
      : null;
    const ftToDate = forecastTotalByDate
      ? stripTime(new Date(request.forecastTotalToDate))
      : null;
    const ft2FromDate = forecastTotal2ByDate
      ? stripTime(new Date(request.forecastTotal2FromDate))
      : null;
    const ft2ToDate = forecastTotal2ByDate
      ? stripTime(new Date(request.forecastTotal2ToDate))
      : null;

    let maxValue = 0;
    for (const row of dateRows) {
      for (const c of config.attributeConfigurations) {
        const v = row[c.attributeName.toLowerCase()];
        if (v == null) continue;
        const num = Number(v);
        if (Number.isFinite(num) && num > maxValue) {
          maxValue = num;
        }
      }
    }

    const totalsByNumberOfMonths = {};
    const yearToGoTotals = {};
    const totalsByDate = {};
    const totals2ByDate = {};

    for (const c of config.attributeConfigurations) {
      const field = c.attributeName.toLowerCase();

      if (c.showSummary) {
        let byMonths = 0;
        let ytg = 0;
        let byDate = 0;
        let byDate2 = 0;

        for (const row of dateRows) {
          const raw = row[field];
          let value;
          if (raw == null) {
            value = 0;
          } else {
            const num = Number(raw);
            value = Number.isFinite(num) ? num : 0;
          }

          const rowDate = stripTime(new Date(row.forecastdate));

          if (totalToMonths > 0 && rowDate <= totalToDate) {
            byMonths += value;
          }
          if (
            (isTrue(request.forecastYearToGo) ||
              isTrue(request.forecastYearToGoPercentageDifference)) &&
            rowDate <= decemberDate
          ) {
            ytg += value;
          }
          if (
            forecastTotalByDate &&
            rowDate >= ftFromDate &&
            rowDate <= ftToDate
          ) {
            byDate += value;
          }
          if (
            forecastTotal2ByDate &&
            rowDate >= ft2FromDate &&
            rowDate <= ft2ToDate
          ) {
            byDate2 += value;
          }
        }

        totalsByNumberOfMonths[field] = byMonths;
        yearToGoTotals[field] = ytg;
        totalsByDate[field] = byDate;
        totals2ByDate[field] = byDate2;
      } else {
        totalsByNumberOfMonths[field] = "";
        yearToGoTotals[field] = "";
        totalsByDate[field] = "";
        totals2ByDate[field] = "";
      }
    }

    totalsByNumberOfMonths.label = `${totalToMonths} Months`;
    totalsByNumberOfMonths.isTotal = true;

    yearToGoTotals.label = `YTG ${currentYear}`;
    yearToGoTotals.isTotal = true;

    totalsByDate.label = request.forecastTotalByDateHeader;
    totalsByDate.isTotal = true;

    totals2ByDate.label = request.forecastTotal2ByDateHeader;
    totals2ByDate.isTotal = true;

    const baseField =
      typeof request.forecastPercentageBaseField === "string" &&
      request.forecastPercentageBaseField.trim().length > 0
        ? request.forecastPercentageBaseField.toLowerCase()
        : "revision2";

    const baseFrom = (dict) =>
      typeof dict[baseField] === "number" ? dict[baseField] : 0;

    const setPercentage = (totals, percentageDict, totalBaseValue, label) => {
      for (const c of config.attributeConfigurations) {
        const field = c.attributeName.toLowerCase();
        if (c.showSummary) {
          let percentageDifference = 0;
          if (totalBaseValue !== 0) {
            percentageDifference = (totals[field] / totalBaseValue) * 100 - 100;
          }
          percentageDict[field] = percentageDifference;
        } else {
          percentageDict[field] = "";
        }
      }
      percentageDict.label = label;
      percentageDict.isPercentage = true;
    };

    const totalsByNumberOfMonthsPercentage = {};
    const yearToGoTotalsPercentage = {};
    const totalsByDatePercentage = {};
    const totals2ByDatePercentage = {};

    if (
      totalToMonths > 0 &&
      isTrue(request.forecastTotalToMonthsPercentageDifference)
    ) {
      setPercentage(
        totalsByNumberOfMonths,
        totalsByNumberOfMonthsPercentage,
        baseFrom(totalsByNumberOfMonths),
        `${totalToMonths} Months % Difference`,
      );
    }
    if (isTrue(request.forecastYearToGoPercentageDifference)) {
      setPercentage(
        yearToGoTotals,
        yearToGoTotalsPercentage,
        baseFrom(yearToGoTotals),
        `YTG ${currentYear} % Difference`,
      );
    }
    if (forecastTotalByDate && isTrue(request.forecastTotalByDatePercentage)) {
      setPercentage(
        totalsByDate,
        totalsByDatePercentage,
        baseFrom(totalsByDate),
        `${request.forecastTotalByDateHeader} % Difference`,
      );
    }
    if (
      forecastTotal2ByDate &&
      isTrue(request.forecastTotal2ByDatePercentage)
    ) {
      setPercentage(
        totals2ByDate,
        totals2ByDatePercentage,
        baseFrom(totals2ByDate),
        `${request.forecastTotal2ByDateHeader} % Difference`,
      );
    }

    const newForecast = [...dateRows];

    if (totalToMonths > 0) {
      newForecast.push(totalsByNumberOfMonths);
    }
    if (
      totalToMonths > 0 &&
      isTrue(request.forecastTotalToMonthsPercentageDifference)
    ) {
      newForecast.push(totalsByNumberOfMonthsPercentage);
    }
    if (isTrue(request.forecastYearToGo)) {
      newForecast.push(yearToGoTotals);
    }
    if (isTrue(request.forecastYearToGoPercentageDifference)) {
      newForecast.push(yearToGoTotalsPercentage);
    }
    if (forecastTotalByDate) {
      newForecast.push(totalsByDate);
      if (isTrue(request.forecastTotalByDatePercentage)) {
        newForecast.push(totalsByDatePercentage);
      }
    }
    if (forecastTotal2ByDate) {
      newForecast.push(totals2ByDate);
      if (isTrue(request.forecastTotal2ByDatePercentage)) {
        newForecast.push(totals2ByDatePercentage);
      }
    }

    return {
      ...data,
      forecast: newForecast,
      maxValue,
    };
  };

  useEffect(() => {
    if (!instance || !accounts || accounts.length === 0) {
      return;
    }

    if (!request || !request.unitOfMeasurements) {
      return;
    }

    const isInitial = !hasInitialData.current;
    if (isInitial) {
      setLoading(true);
    } else {
      setTableLoading(true);
    }

    const accessTokenRequest = {
      ...tokenRequest,
      account: accounts[0],
    };

    instance
      .acquireTokenSilent(accessTokenRequest)
      .then((accessTokenResponse) => {
        const accessToken = accessTokenResponse.accessToken;
        (async () => {
          const data = await forecastApi
            .list(accessToken, {
              ...request,
              includePendingOverrides: includePendingOverrides,
            })
            .then((data) => {
              setError(false);
              setAuthorizationError(null);
              hasInitialData.current = true;
              setDataVersion((v) => v + 1);
              setStatusMessages((prev) =>
                prev.filter((m) => m.id !== "fetch-error"),
              );
              return data;
            })
            .catch((e) => {
              if (e && e?.response?.status === 403) {
                setAuthorizationError(e);
              } else {
                setError(e);
                if (!isInitial) {
                  pushStatus(
                    "fetch-error",
                    "error",
                    "Couldn't refresh data. Please try again.",
                  );
                }
              }
              console.log(e);
            })
            .finally(() => {
              if (isInitial) {
                setLoading(false);
              } else {
                setTableLoading(false);
              }
            });
          if (data != null) {
            setResults(data);
          }
        })();
      })
      .catch((error) => {
        setError(true);
        if (!isInitial) {
          pushStatus(
            "fetch-error",
            "error",
            "Couldn't refresh data. Please try again.",
          );
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, instance, accounts, refetchNonce, includePendingOverrides]);

  const handleOptionsModel = () => {
    setShowOptionsModal((showOptionsModal) => {
      return !showOptionsModal;
    });
  };

  const anyOptionsSelected = () => {
    return enableOptions && options.find((o) => o.isSelected);
  };

  useEffect(() => {
    const timers = pollingTimersRef.current; // snapshot inside the effect

    return () => {
      timers.forEach((timerId) => clearInterval(timerId));
      timers.clear();
    };
  }, []);

  const handleDismissStatus = (id) => {
    if (id === "fetch-error") {
      setError(false);
    }
    setStatusMessages((prev) => prev.filter((m) => m.id !== id));
  };

  const pushStatus = (id, type, text) => {
    setStatusMessages((prev) => [
      ...prev.filter((m) => m.id !== id),
      { id, type, text },
    ]);
  };

  const handleRefresh = () => {
    setStatusMessages((prev) => prev.filter((m) => m.type !== "success"));
    setRefetchNonce((n) => n + 1);
  };

  const startPolling = (overrideId) => {
    const POLL_INTERVAL_MS = 30000;
    const MAX_CONSECUTIVE_FAILURES = 5;
    let failures = 0;

    const stop = () => {
      const timerId = pollingTimersRef.current.get(overrideId);
      if (timerId != null) {
        clearInterval(timerId);
        pollingTimersRef.current.delete(overrideId);
      }
    };

    const tick = async () => {
      if (!instance || !accounts || accounts.length === 0) {
        return;
      }
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          ...tokenRequest,
          account: accounts[0],
        });
        const result = await forecastApi.getDisaggregationStatus(
          tokenResponse.accessToken,
          overrideId,
        );
        failures = 0;
        const status = String(result ?? "").toLowerCase();
        if (status === "completed") {
          stop();
          pushStatus(overrideId, "success", "Forecast override completed");
        } else if (status === "failed") {
          stop();
          pushStatus(overrideId, "error", "Forecast override failed");
        }
      } catch (e) {
        console.log(e);
        failures += 1;
        if (failures >= MAX_CONSECUTIVE_FAILURES) {
          stop();
          pushStatus(
            overrideId,
            "error",
            "Unable to confirm override status. Please refresh.",
          );
        }
      }
    };

    const timerId = setInterval(tick, POLL_INTERVAL_MS);
    pollingTimersRef.current.set(overrideId, timerId);
  };

  const handleSave = (
    timeHorizonValuesToBeCreated,
    attributeName,
    entityName,
    areaCode,
    item,
    editedByForecastDate,
  ) => {
    const lockEdit = checkDataEditLock(config.enableEditLock, setErrorMessages);

    if (lockEdit) {
      return;
    }

    if (timeHorizonValuesToBeCreated.length <= 0) {
      let errors = [];
      errors.push({
        name: attributeName,
        value:
          "Values are not changed, please update atleast one month to save Or Select Apply to time buckets as All to save all time buckets",
      });
      setErrorMessages(errors);
      return;
    }
    setErrorMessages([]);
    const filters =
      selectedSearchData != null &&
      selectedSearchData.selectedFilterData != null
        ? selectedSearchData.selectedFilterData.map((filterData) => {
            return {
              parent: filterData.path,
              field: filterData.field,
              items: filterData.items,
            };
          })
        : null;

    let itemAreaSelections = [];

    if (item != null && item.itemCode != null && areaCode != null) {
      itemAreaSelections.push({
        itemCode: item.itemCode,
        areaCode: areaCode,
        isSelected: true,
      });
    }

    const overrideItemAreaSelection = {
      overrideId: 0,
      filters: filters,
      itemAreaSelectionContainer: {
        selectAll: false,
        itemAreaSelections: itemAreaSelections,
      },
      additionalInfo: location.search,
    };

    let saveRequest = {
      attributeName: attributeName,
      entityName: entityName,
      uoM: selectedSearchData.unitOfMeasurements,
      overrideTimeHorizon: timeHorizonValuesToBeCreated,
      overrideItemAreaSelection: overrideItemAreaSelection,
      itemAreaLevel: request.itemAreaLevel,
      currency: request.currency,
    };

    if (anyOptionsSelected()) {
      const selectedOption = options.find((option) => option.isSelected);

      switch (selectedOption.value) {
        case overrideOptions.RemoveOverrides:
          saveRequest = {
            ...saveRequest,
            removeOverrides: selectedOption.isSelected,
          };
          break;

        default:
          break;
      }
    }

    if (!instance || !accounts || accounts.length === 0) {
      return;
    }

    setIsSaving(true);

    const accessTokenRequest = {
      ...tokenRequest,
      account: accounts[0],
    };

    instance
      .acquireTokenSilent(accessTokenRequest)
      .then((accessTokenResponse) => {
        const accessToken = accessTokenResponse.accessToken;
        (async () => {
          await forecastApi
            .override(accessToken, saveRequest)
            .then((data) => {
              setError(null);
              setErrorMessages([]);
              if (editedByForecastDate) {
                const key = attributeName.toLowerCase();
                setResults((prev) => {
                  if (!prev || !Array.isArray(prev.forecast)) {
                    return prev;
                  }
                  return withTotals(
                    withCalculatedFields({
                      ...prev,
                      forecast: prev.forecast.map((f) => {
                        if (
                          !Object.prototype.hasOwnProperty.call(
                            editedByForecastDate,
                            f.forecastdate,
                          )
                        ) {
                          return f;
                        }
                        const raw = editedByForecastDate[f.forecastdate];
                        const num = Number(raw);
                        if (raw === "" || raw == null || Number.isNaN(num)) {
                          return f;
                        }
                        return { ...f, [key]: num };
                      }),
                    }),
                  );
                });
              }
              const overrideId = data?.id;
              if (overrideId != null) {
                startPolling(overrideId);
              }
              setSaveSuccessNonce((n) => n + 1);
              return data;
            })
            .catch((error) => {
              console.log(error);
              if (
                error.response?.status === 400 &&
                error.response?.data?.errors
              ) {
                let errors = [];
                for (const [key, value] of Object.entries(
                  error.response.data.errors,
                )) {
                  errors.push({ name: key, value: value });
                }
                setErrorMessages(errors);
              } else {
                setError(error);
              }
            })
            .finally(() => setIsSaving(false));
        })();
      })
      .catch((error) => {
        setIsSaving(false);
        setError(error);
      });
  };

  const hasItemAreaForecast = () => {
    return (
      results != null &&
      results.itemAreaForecast != null &&
      results.itemAreaForecast.forecast != null
    );
  };

  const hasTotalForecast = () => {
    return (
      results != null && results.forecast != null && results.forecast.length > 0
    );
  };

  const hasForecast = () => {
    return hasTotalForecast() || hasItemAreaForecast();
  };

  const handleSavingOfTimeBuckets = (value) => {
    setSaveOnlyChangedTimeBuckets(value);
  };

  const getExcelData = (forecastData) => {
    const excelData = [];

    const attributeNameHeader =
      config.attributeDisplayNameHeader != null
        ? config.attributeDisplayNameHeader
        : "Name";

    config.attributeConfigurations.forEach((c) => {
      let dataByHeader = {
        [attributeNameHeader]: c.displayName,
      };

      forecastData.forEach((item) => {
        dataByHeader[item.label] = item[c.attributeName.toLowerCase()];
      });

      excelData.push(dataByHeader);
    });

    return excelData;
  };

  const handleExportToExcel = () => {
    const sheetData = getExcelData(results?.forecast);
    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Forecast");
    XLSX.writeFile(workbook, "Forecast.xlsx");
  };

  if (error && !hasInitialData.current) {
    return <Error error={error} />;
  }

  return (
    <React.Fragment>
      {statusMessages.length > 0
        ? createPortal(
            <div className="forecast-status-toast-stack">
              {statusMessages.map((m) => (
                <StatusToast
                  key={m.id}
                  id={m.id}
                  type={m.type}
                  text={m.text}
                  onDismiss={handleDismissStatus}
                  onRefresh={handleRefresh}
                />
              ))}
            </div>,
            document.body,
          )
        : null}
      <div className="row">
        {errorMessages && errorMessages.length > 0 ? (
          <div className="col-12 ms-3">
            {errorMessages.map((e, i) => {
              return (
                <div key={i} className="mb-2 text-danger">
                  {e.value}
                </div>
              );
            })}
          </div>
        ) : (
          ""
        )}
      </div>

      <div className="col-12">
        {!hideBackButton ? (
          <div className="flex py-2">
            <div className="flex">
              <button
                type="button"
                className="link"
                onClick={() => handleBack()}
              >
                <span className="arrow-icon back">
                  <BackIcon />
                </span>
                <span className="font-weight-medium">Back</span>
              </button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className="col-12">
            <Spinner></Spinner>
          </div>
        ) : (
          <React.Fragment>
            {hasForecast() ? (
              <React.Fragment>
                {showChart ? (
                  <div
                    className={`row${
                      tableLoading || isSaving ? " db-forecast-loading" : ""
                    }`}
                  >
                    <ForecastChart
                      data={results.forecast.filter(
                        (f) => !f.isTotal && !f.isPercentage,
                      )}
                      config={config}
                    />
                  </div>
                ) : null}
                {isTrue(config.showForecastTotal) &&
                isTrue(config.isForecastTotalEditable) &&
                hasTotalForecast() ? (
                  <div
                    className={`row mt-2${
                      tableLoading || isSaving ? " db-forecast-loading" : ""
                    }`}
                  >
                    <div
                      className="col-12"
                      key={`forecasttable-${dataVersion}`}
                    >
                      <div className="card shadow-sm rounded-extra-lg mb-4">
                        {config.totalTableHeader != null ? (
                          <h4 className="pt-3 ps-4">
                            {config.totalTableHeader}
                          </h4>
                        ) : (
                          ""
                        )}
                        <div className="card-body">
                          <div className="db-forecast-toolbar">
                            <PendingOverridesToggle
                              checked={includePendingOverrides}
                              onChange={handlePendingOverridesChange}
                              disabled={tableLoading || isSaving}
                            />
                            <div className="db-forecast-toolbar__switch">
                              <button
                                type="button"
                                className={`db-forecast-toolbar__switch-button${
                                  !saveOnlyChangedTimeBuckets
                                    ? " db-forecast-toolbar__switch-button--active"
                                    : ""
                                }`}
                                onClick={() => handleSavingOfTimeBuckets(false)}
                                aria-pressed={!saveOnlyChangedTimeBuckets}
                                title="Save all time buckets"
                              >
                                <CheckCheck size={16} strokeWidth={2} />
                              </button>
                              <button
                                type="button"
                                className={`db-forecast-toolbar__switch-button${
                                  saveOnlyChangedTimeBuckets
                                    ? " db-forecast-toolbar__switch-button--active"
                                    : ""
                                }`}
                                onClick={() => handleSavingOfTimeBuckets(true)}
                                aria-pressed={saveOnlyChangedTimeBuckets}
                                title="Save only changed time buckets"
                              >
                                <Pencil size={16} strokeWidth={2} />
                              </button>
                            </div>
                            <button
                              type="button"
                              className="db-forecast-toolbar__icon-button"
                              onClick={handleOptionsModel}
                              disabled={!enableOptions}
                              title="Options"
                            >
                              <List size={16} strokeWidth={2} />
                            </button>
                            {isTrue(config.enableForecastTotalExport) ? (
                              <button
                                type="button"
                                className="db-forecast-toolbar__icon-button"
                                onClick={handleExportToExcel}
                                title="Download as Excel (.xlsx)"
                              >
                                <FileSpreadsheet size={16} strokeWidth={2} />
                              </button>
                            ) : null}
                          </div>
                          <div className="ps-2">
                            <OverrideOptions
                              showOptionsModal={showOptionsModal}
                              handleOptionsModel={handleOptionsModel}
                              setOptions={setOptions}
                              options={options}
                              setSelectedOptions={setSelectedOptions}
                            />
                          </div>
                          <div className="mb-2 table-responsive scrollbar horizontal">
                            <div className="scrollbar-content">
                              <ForecastTable
                                data={results.forecast}
                                config={config}
                                handleSave={handleSave}
                                currentAction={currentAction}
                                saveOnlyChangedTimeBuckets={
                                  saveOnlyChangedTimeBuckets
                                }
                                forecastRequest={{
                                  request: request,
                                  forecastDates: results.forecast.map(
                                    (i) => i.label,
                                  ),
                                }}
                                anyOptionsSelected={anyOptionsSelected}
                                isUserAuthorized={isUserAuthorized}
                                setEnableOptions={setEnableOptions}
                                selectedOptions={selectedOptions}
                                isSaving={isSaving}
                                saveSuccessNonce={saveSuccessNonce}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
                {isTrue(config.showItemAreaForecast) &&
                isTrue(config.isItemAreaForecastEditable) &&
                hasItemAreaForecast() ? (
                  <div
                    className={`row mt-4${
                      tableLoading || isSaving ? " db-forecast-loading" : ""
                    }`}
                  >
                    <div
                      className="col-12"
                      key={`itemarea-forecasttable-${dataVersion}`}
                    >
                      <div className="card shadow-sm rounded-extra-lg">
                        <h4 className="pt-3 ps-4">
                          {config.itemAreaTableHeader != null
                            ? config.itemAreaTableHeader
                            : "Forecast"}
                        </h4>
                        <div className="card-body">
                          <div className="mb-2 table-responsive scrollbar horizontal">
                            <div className="scrollbar-content">
                              <ItemAreaForecastTable
                                data={results.itemAreaForecast}
                                config={config}
                                handleSave={handleSave}
                                currentAction={currentAction}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </React.Fragment>
            ) : (
              <div className="col-12 mt-3">
                <div className="text-center text-danger">
                  No forecast data available for the selected filters
                </div>
              </div>
            )}
            <div></div>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
};

export default DbForecast;
